import {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { ApplicationStatus, EmployerApplicantSummary } from '@tabbio/types';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { ApplicantsPagination } from './applicants-pagination.web';
import { LibraryListSkeleton } from '../library/LibraryListSkeleton.web';
import { useEmployerApplicants } from '../../hooks/useJobsApi';
import { formatRelativeDate } from '../../lib/jobs-format';

type ApplicantTab = 'all' | ApplicationStatus;

const PAGE_SIZE = 20;
const EMPTY_COUNTS = {
  total: 0,
  applied: 0,
  reviewed: 0,
  shortlisted: 0,
  interview: 0,
  offered: 0,
  rejected: 0,
  withdrawn: 0,
};
const APPLICANT_TABS: ApplicantTab[] = [
  'all',
  'applied',
  'reviewed',
  'shortlisted',
  'interview',
  'offered',
  'rejected',
];

// ─── Avatar ──────────────────────────────────────────────────────────────────

function Avatar({ name, imageUrl }: { name?: string | null; imageUrl?: string | null }) {
  const [failed, setFailed] = useState(false);
  const initials =
    (name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?';
  const showImage = Boolean(imageUrl) && !failed;

  if (showImage) {
    return (
      <img
        src={imageUrl as string}
        alt={name ?? ''}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        flexShrink: 0,
        background: 'rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--text-secondary)',
      }}
    >
      {initials}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ApplicationStatus }) {
  const { t } = useTranslation('common');
  return (
    <span className="tabbio-menu-badge" style={{ flexShrink: 0 }}>
      {t(`jobApplicants.status.${status}` as never)}
    </span>
  );
}

// ─── Stage + CV pill ─────────────────────────────────────────────────────────

function Pill({ label }: { label: string }) {
  return <span className="tabbio-menu-badge">{label}</span>;
}

// ─── Applicant row ────────────────────────────────────────────────────────────

function ApplicantRow({
  applicant,
  onClick,
}: {
  applicant: EmployerApplicantSummary;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation('common');

  const meta = [
    applicant.candidate.location ||
      applicant.candidate.contactEmail ||
      applicant.candidate.email,
    t('jobApplicants.appliedAgo', {
      time: formatRelativeDate(applicant.appliedAt, i18n.language),
    }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="tabbio-list-row"
      style={{
        alignItems: 'flex-start',
        minHeight: 84,
        padding: '16px 20px',
      }}
    >
      <Avatar name={applicant.candidate.name} imageUrl={applicant.candidate.image} />

      <div className="tabbio-list-row-body">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {applicant.candidate.name}
          </span>
          <StatusBadge status={applicant.status} />
        </div>

        <div className="tabbio-list-row-sub">
          {applicant.candidate.title || t('jobApplicants.noTitle')}
        </div>

        <div
          style={{
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <Pill label={t(`jobApplicants.stage.${applicant.libraryStage}` as never)} />
          {applicant.submittedCv?.title ? (
            <Pill label={applicant.submittedCv.title} />
          ) : null}
        </div>

        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {meta}
        </div>
      </div>
    </button>
  );
}

// ─── Empty / error state ──────────────────────────────────────────────────────

function StateCard({
  title,
  subtitle,
  icon = 'inbox',
}: {
  title: string;
  subtitle: string;
  icon?: string;
}) {
  return (
    <div
      style={{
        padding: '48px 16px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <span
        aria-hidden
        style={{ color: 'var(--text-tertiary)', marginBottom: 4 }}
      >
        <MaterialSymbol name={icon} size={40} />
      </span>
      <span
        style={{
          fontSize: 18,
          fontWeight: 500,
          color: 'var(--text-primary)',
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontSize: 14,
          lineHeight: 1.5,
          color: 'var(--text-secondary)',
          maxWidth: 420,
        }}
      >
        {subtitle}
      </span>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export function EmployerApplicantsScreen({
  jobId,
  detailHrefBuilder,
  headerSlot,
}: {
  jobId: string;
  detailHrefBuilder?: (jobId: string, applicationId: string) => string;
  headerSlot?: ReactNode;
}) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<ApplicantTab>('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const deferredQuery = useDeferredValue(query.trim());

  useEffect(() => {
    setPage(1);
  }, [activeTab, deferredQuery]);

  const applicantsQuery = useEmployerApplicants(
    jobId,
    {
      query: deferredQuery || undefined,
      statuses: activeTab === 'all' ? undefined : [activeTab],
      page,
      pageSize: PAGE_SIZE,
    },
    Boolean(jobId),
  );

  const isTabSwitching = applicantsQuery.isPlaceholderData;
  const applicants = isTabSwitching ? [] : (applicantsQuery.data?.applicants ?? []);
  const counts = applicantsQuery.data?.counts ?? EMPTY_COUNTS;
  const totalPages = isTabSwitching ? 1 : (applicantsQuery.data?.totalPages ?? 1);
  const currentPage = isTabSwitching ? page : (applicantsQuery.data?.page ?? page);
  const hasSearch = Boolean(query.trim());

  const summaryText = useMemo(
    () =>
      t('jobApplicants.heroOverview', {
        total: counts.total,
        reviewed: counts.reviewed,
        shortlisted: counts.shortlisted,
        interview: counts.interview,
      }),
    [counts.interview, counts.reviewed, counts.shortlisted, counts.total, t],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Header slot: back button + role title (provided by parent) */}
      {headerSlot}

      {/* Summary line */}
      <div
        style={{
          padding: '0 8px',
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--text-secondary)',
        }}
      >
        {summaryText}
      </div>

      {/* Search bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          height: 48,
          paddingInline: 18,
          borderRadius: 9999,
          background: 'rgba(0,0,0,0.04)',
        }}
      >
        <MaterialSymbol
          name="search"
          size={20}
          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('jobApplicants.searchPlaceholder')}
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 15,
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
          }}
        />
        {hasSearch ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear"
            style={{
              width: 28,
              height: 28,
              border: 0,
              borderRadius: '50%',
              background: 'transparent',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaterialSymbol name="close" size={18} />
          </button>
        ) : null}
      </div>

      {/* Status filter chips */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '2px 0',
          scrollbarWidth: 'none',
        }}
      >
        {APPLICANT_TABS.map((tab) => {
          const active = activeTab === tab;
          const count =
            tab === 'all'
              ? counts.total
              : (counts[tab as keyof typeof EMPTY_COUNTS] ?? 0);
          const label =
            tab === 'all'
              ? t('jobApplicants.filters.all')
              : t(`jobApplicants.status.${tab}` as never);
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={active ? 'tabbio-chip active' : 'tabbio-chip'}
              style={{ flexShrink: 0 }}
            >
              {label}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--text-tertiary)',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Applicant list */}
      {applicantsQuery.isLoading || isTabSwitching ? (
        <LibraryListSkeleton count={5} />
      ) : applicantsQuery.error ? (
        <StateCard
          icon="error_outline"
          title={t('jobApplicants.loadErrorTitle')}
          subtitle={t('jobApplicants.loadErrorSubtitle')}
        />
      ) : !applicants.length ? (
        <StateCard
          icon={hasSearch ? 'search_off' : 'inbox'}
          title={
            hasSearch
              ? t('jobApplicants.emptyFilteredTitle')
              : t('jobApplicants.emptyTitle')
          }
          subtitle={
            hasSearch
              ? t('jobApplicants.emptyFilteredSubtitle')
              : t('jobApplicants.emptySubtitle')
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {applicants.map((applicant) => (
            <ApplicantRow
              key={applicant.applicationId}
              applicant={applicant}
              onClick={() =>
                router.push(
                  (detailHrefBuilder?.(jobId, applicant.applicationId) ??
                    `/(employer)/jobs/${jobId}/applicants/${applicant.applicationId}`) as never,
                )
              }
            />
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <ApplicantsPagination
          page={currentPage}
          totalPages={totalPages}
          onChange={(next) => {
            setPage(next);
            if (typeof window !== 'undefined') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        />
      ) : null}
    </div>
  );
}
